"use client"

import Image from "next/image"
import Link from "next/link"
import { ArrowLeft, Compass, Search } from "lucide-react"

import { Button } from "@/components/ui/button"
import { brand } from "@/lib/brand"

const quickLinks = [
  { label: "Go to dashboard", href: "/dashboard" },
  { label: "Explore modules", href: "/modules" },
  { label: "Visit support centre", href: "/support" },
] as const

export default function NotFound() {
  const supportEmail = brand.supportEmail

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <div className="relative w-full max-w-3xl overflow-hidden rounded-2xl border border-border bg-card shadow-2xl">
        <div
          className="absolute -top-24 left-1/2 h-64 w-64 -translate-x-1/2 rounded-full bg-primary/20 blur-3xl"
          aria-hidden="true"
        />

        <div className="relative grid gap-10 px-10 py-12">
          <header className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                <Image
                  src={brand.logoPath}
                  alt={`${brand.name} emblem`}
                  width={48}
                  height={48}
                  className="rounded-md border border-primary/30 object-cover"
                  priority
                />
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {brand.organization}
                </p>
                <p className="text-lg font-semibold text-primary">Navigation assistant</p>
              </div>
            </div>

            <span className="rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary">
              Page not found
            </span>
          </header>

          <section className="grid gap-6">
            <div className="flex flex-col gap-4 rounded-2xl border border-border/60 bg-background/60 p-6">
              <div className="flex items-center gap-3 text-primary">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                  <Compass className="h-6 w-6" aria-hidden="true" />
                </span>
                <div>
                  <h1 className="text-2xl font-semibold text-foreground">We could not locate that workspace</h1>
                  <p className="text-sm text-muted-foreground">
                    The address you entered is unavailable or has been archived. Let&apos;s guide you back to an active
                    module.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 rounded-xl border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-background/80">
                  <Search className="h-5 w-5 text-primary" aria-hidden="true" />
                </span>
                <p>
                  Double-check the URL or choose a quick link below to continue your session without interruption.
                </p>
              </div>
            </div>

            <div className="grid gap-3 rounded-2xl border border-border/60 bg-primary/5 p-6">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Quick destinations</p>
              <div className="grid gap-3 sm:grid-cols-3">
                {quickLinks.map((link) => (
                  <Button key={link.href} asChild variant="outline" className="h-auto flex-col gap-1 border-primary/50 text-center text-sm text-primary hover:bg-primary/10">
                    <Link href={link.href}>{link.label}</Link>
                  </Button>
                ))}
              </div>
            </div>
          </section>

          <div className="flex flex-col gap-4 border-t border-border pt-6 text-sm text-muted-foreground">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Image src="/images/analyze.svg" alt="Personnel analytics" width={32} height={32} className="h-8 w-8" />
                <span>Integrated Personnel Console</span>
              </div>
              <Button variant="ghost" asChild className="gap-2 text-primary hover:text-primary">
                <Link href="/">
                  <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                  Return to home
                </Link>
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Need help? Reach the digital operations desk at{" "}
              <a href={`mailto:${supportEmail}`} className="text-primary hover:underline">
                {supportEmail}
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
